import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';

import { DefaultTableContainer, TableContainerWithSorting } from 'components';
import { ComponentFilterProps, withFilter } from 'hoc/withFilter';
import { SortDirection } from 'app-constants/table';
import { useTranslation } from 'react-i18next';
import useGetPlatforms from 'pages/Wim/hooks/use-get-platforms';
import { INITIAL_TRANSITS_FILTER_VALUES } from 'store/wim/filter/transits';
import { ComparativeAnalysisFilterValues } from 'store/wim/comparative-analysis/type';
import wimSelectors from 'store/wim/selectors';
import { wimActions } from 'store/wim/slices';
import { useAppDispatch, useAppSelector } from 'hooks/store-hooks';
import dayjs from 'dayjs';
import { useUrlParams } from 'hooks/use-url-params';
import { Table } from 'antd';

import { getTransitsAnalysisTableColumns, getChildrenColumns } from './table-schema';
import ComparativeAnalysisFilterPanel from './ComparativeAnalysisFilterPanel';
import { queryParamsToFilterValues } from './ComparativeAnalysisFilterPanel/schema';
import ComingSoon from '../../../components/Common/ComingSoon';
import { TransitsAnalysisTableRow } from './type';
import { useHeaderWithFiltersRendererWithExport } from './HeaderFilterWithExportButton';

const PAGE_SIZE = 25;
const SCROLL_THRESHOLD_PX = 400;

const ComparativeAnalysisPage: React.FC<ComponentFilterProps<ComparativeAnalysisFilterValues>> = ({
  filterManager,
}) => {
  useGetPlatforms();

  const { isFilterSubmitClicked } = filterManager;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { queryUrlParams } = useUrlParams({});

  const columns = useMemo(() => Object.values(getTransitsAnalysisTableColumns()), []);
  const childrenColumns = useMemo(() => Object.values(getChildrenColumns()), []);

  const startInitialDate = dayjs().subtract(1, 'day').valueOf();
  const endInitialDate = dayjs().valueOf();

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  useHeaderWithFiltersRendererWithExport({ filterManager, selectedRowKeys });

  const transits = useAppSelector(wimSelectors.comparativeAnalysis.getAnalysisTransits);
  const isLoading = useAppSelector(wimSelectors.comparativeAnalysis.isLoading);
  const [data, setData] = useState<TransitsAnalysisTableRow[]>([]);

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const isFetchingMoreRef = useRef(false);
  const lastIdRef = useRef<number | null>(null);
  const reachedEndRef = useRef(false);

  const apiLast = Boolean(transits?.data?.last);

  const buildRequestPayload = useCallback(
    (startId?: number) => {
      const { start, end, ...rest } = queryUrlParams.filter ?? {};

      return {
        from: start ?? startInitialDate,
        to: end ?? endInitialDate,
        limit: PAGE_SIZE,
        ...(queryUrlParams?.sort?.key && queryUrlParams?.sort?.value
          ? {
              sort: {
                [queryUrlParams.sort.key]:
                  queryUrlParams.sort.value === 'descend' ? SortDirection.DESCEND : SortDirection.ASCEND,
              },
            }
          : {}),
        ...(startId ? { startId } : {}),
        ...rest,
      };
    },
    [queryUrlParams.filter, startInitialDate, endInitialDate, queryUrlParams?.sort?.key, queryUrlParams?.sort?.value]
  );

  useEffect(() => {
    setData([]);
    lastIdRef.current = null;
    reachedEndRef.current = false;
    isFetchingMoreRef.current = false;

    dispatch(wimActions.comparativeAnalysis.clearTransits());
    dispatch(wimActions.comparativeAnalysis.requestTransits(buildRequestPayload()));
  }, [dispatch, isFilterSubmitClicked, queryUrlParams?.sort?.key, queryUrlParams?.sort?.value, queryUrlParams?.filter]);

  useEffect(() => {
    const content = (transits?.data?.content ?? []) as TransitsAnalysisTableRow[];
    if (!content) return;

    const isEnd = apiLast || content.length < PAGE_SIZE;

    setData((prev) => {
      if (prev.length === 0) return content;

      const prevIds = new Set(prev.map((x: any) => x.id));
      const next = content.filter((x: any) => !prevIds.has(x.id));
      return [...prev, ...next];
    });

    const lastItem = content[content.length - 1];
    if (lastItem?.id != null) {
      lastIdRef.current = Number(lastItem.id);
    }

    reachedEndRef.current = isEnd;
    isFetchingMoreRef.current = false;
  }, [transits?.data?.content, apiLast]);

  const loadMore = useCallback(() => {
    if (isLoading) return;
    if (reachedEndRef.current) return;
    if (isFetchingMoreRef.current) return;

    const startId = lastIdRef.current;
    if (!startId) return;

    isFetchingMoreRef.current = true;
    dispatch(wimActions.comparativeAnalysis.requestTransits(buildRequestPayload(startId)));
  }, [dispatch, buildRequestPayload, isLoading]);

  const getScrollParent = (el: HTMLElement | null): HTMLElement | Window => {
    let node = el;

    while (node && node !== document.body) {
      const style = window.getComputedStyle(node);
      // eslint-disable-next-line prefer-destructuring
      const overflowY = style.overflowY;
      const canScroll = (overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight;

      if (canScroll) return node;
      node = node.parentElement;
    }

    return window;
  };

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return () => {};

    const scrollParent = getScrollParent(wrapper);

    const onScroll = () => {
      if (reachedEndRef.current) return;
      if (isLoading) return;

      const scrollTop =
        scrollParent === window
          ? document.scrollingElement?.scrollTop ?? window.scrollY ?? 0
          : (scrollParent as HTMLElement).scrollTop;

      const scrollHeight =
        scrollParent === window
          ? document.scrollingElement?.scrollHeight ?? document.body.scrollHeight
          : (scrollParent as HTMLElement).scrollHeight;

      const clientHeight = scrollParent === window ? window.innerHeight : (scrollParent as HTMLElement).clientHeight;

      const distanceToBottom = scrollHeight - (scrollTop + clientHeight);

      if (distanceToBottom <= SCROLL_THRESHOLD_PX) {
        loadMore();
      }
    };

    (scrollParent === window ? window : (scrollParent as HTMLElement)).addEventListener('scroll', onScroll, {
      passive: true,
    } as any);

    return () => {
      (scrollParent === window ? window : (scrollParent as HTMLElement)).removeEventListener('scroll', onScroll as any);
    };
  }, [isLoading, loadMore]);

  const comingSoon = false;

  return (
    <div style={{ height: '100%' }} ref={wrapperRef}>
      {comingSoon && <ComingSoon />}

      {!comingSoon && (
        <TableContainerWithSorting<TransitsAnalysisTableRow>
          loading={isLoading}
          dataSource={data ? (data as any) : []}
          columns={columns}
          showSorterTooltip={false}
          scroll={{ y: Infinity }}
          size="small"
          rowKey={(record) => record.id}
          footer={() => {
            return null;
          }}
          expandable={{
            expandedRowRender: (record) => {
              const related = record.relatedVehicleEvents ?? [];
              return (
                <div style={{ padding: '8px 8px 8px 24px' }}>
                  <Table
                    size="small"
                    bordered={false}
                    pagination={false}
                    showHeader
                    columns={childrenColumns}
                    dataSource={related as any}
                    rowKey={(r: any) => r.id ?? r.uuid}
                  />
                </div>
              );
            },
            rowExpandable: (record) => Boolean(record.relatedVehicleEvents?.length),
          }}
        />
      )}
    </div>
  );
};

export default withFilter<ComparativeAnalysisFilterValues>({
  filterProps: {
    initialState: INITIAL_TRANSITS_FILTER_VALUES,
    formatUrlParams: queryParamsToFilterValues,
    setFilterValuesAction: wimActions.comparativeAnalysis.setFilterValues,
  },
  FilterContent: ComparativeAnalysisFilterPanel,
})(ComparativeAnalysisPage);
